import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Check, Pencil } from "lucide-react";
import {
  listarFormularios, listarRespostas, salvarResposta, opcoesDoCampo,
  CampoBriefing, FormularioBriefing,
} from "../services/briefings";

const OUTRO = "__outro__";

/**
 * Seção de BRIEFING de uma tarefa ou projeto. Carrega o formulário vinculado ao
 * tipo (tarefa ou projeto), mostra o que já foi respondido e permite preencher/
 * editar. As respostas ficam guardadas no próprio card — é o ponto do briefing
 * nativo (vs. um Google Forms solto).
 */
export function BriefingSecao({ escopo, alvoId, tipoTarefaId, tipoProjetoSlug, podeEditar = true }: {
  escopo: "tarefa" | "projeto";
  alvoId: string;
  tipoTarefaId?: string | null;
  tipoProjetoSlug?: string | null;
  podeEditar?: boolean;
}) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState(false);

  const { data: formularios = [] } = useQuery({
    queryKey: ["briefings", "para", escopo, tipoTarefaId || tipoProjetoSlug || "-"],
    queryFn: () => listarFormularios(
      escopo === "tarefa" ? { tipoTarefaId: tipoTarefaId || undefined } : { tipoProjetoSlug: tipoProjetoSlug || undefined },
    ),
    enabled: escopo === "tarefa" ? !!tipoTarefaId : !!tipoProjetoSlug,
  });
  const { data: respostas = [] } = useQuery({
    queryKey: ["briefing-respostas", escopo, alvoId],
    queryFn: () => listarRespostas(escopo, alvoId),
  });

  // Formulário a usar: o vinculado ao tipo (ou o que já foi respondido).
  const form: FormularioBriefing | undefined = formularios[0] || respostas[0]?.formulario;
  const respostaAtual = useMemo(
    () => respostas.find((r) => r.formularioId === form?.id),
    [respostas, form?.id],
  );

  if (!form) return null; // sem formulário pro tipo → seção não aparece

  return (
    <div className="mt-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5">
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1 mb-1.5">
        <ClipboardList size={12} /> {form.nome}
        {respostaAtual && !editando && (
          <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 px-1.5 py-px rounded-full">preenchido</span>
        )}
        {podeEditar && !editando && (
          <button type="button" onClick={() => setEditando(true)} className="ml-auto text-blue-600 hover:underline font-medium inline-flex items-center gap-0.5">
            <Pencil size={11} /> {respostaAtual ? "Editar" : "Preencher"}
          </button>
        )}
      </div>

      {editando ? (
        <FormularioPreenchimento
          form={form}
          valoresIniciais={respostaAtual ? seguroParse(respostaAtual.valores) : {}}
          onCancelar={() => setEditando(false)}
          onSalvo={() => {
            qc.invalidateQueries({ queryKey: ["briefing-respostas", escopo, alvoId] });
            setEditando(false);
          }}
          escopo={escopo}
          alvoId={alvoId}
        />
      ) : respostaAtual ? (
        <ResumoRespostas form={form} valores={seguroParse(respostaAtual.valores)} />
      ) : (
        <p className="text-[11px] text-slate-400">Briefing ainda não preenchido.</p>
      )}
    </div>
  );
}

function seguroParse(json: string): Record<string, any> {
  try { return JSON.parse(json) || {}; } catch { return {}; }
}

function ResumoRespostas({ form, valores }: { form: FormularioBriefing; valores: Record<string, any> }) {
  return (
    <ul className="space-y-1.5">
      {form.campos.map((c) => {
        const v = valores[c.id || ""];
        const texto = Array.isArray(v) ? v.filter(Boolean).join(", ") : (v ?? "");
        return (
          <li key={c.id} className="text-xs">
            <span className="text-slate-500 dark:text-slate-400">{c.rotulo}: </span>
            <span className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{texto || <em className="text-slate-400">—</em>}</span>
          </li>
        );
      })}
    </ul>
  );
}

function FormularioPreenchimento({ form, valoresIniciais, escopo, alvoId, onCancelar, onSalvo }: {
  form: FormularioBriefing;
  valoresIniciais: Record<string, any>;
  escopo: "tarefa" | "projeto";
  alvoId: string;
  onCancelar: () => void;
  onSalvo: () => void;
}) {
  const [valores, setValores] = useState<Record<string, any>>(valoresIniciais);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => { setValores(valoresIniciais); }, [form.id]);

  const salvarM = useMutation({
    mutationFn: () => salvarResposta({
      formularioId: form.id,
      ...(escopo === "tarefa" ? { tarefaId: alvoId } : { projetoId: alvoId }),
      valores,
    }),
    onSuccess: onSalvo,
  });

  function set(campoId: string, v: any) { setValores((old) => ({ ...old, [campoId]: v })); }

  function submeter() {
    // Valida obrigatórias
    const faltando = form.campos.filter((c) => {
      if (!c.obrigatorio) return false;
      const v = valores[c.id || ""];
      return Array.isArray(v) ? v.filter(Boolean).length === 0 : !String(v ?? "").trim();
    });
    if (faltando.length > 0) {
      setErro(`Responda: ${faltando.map((c) => c.rotulo).join(", ")}`);
      return;
    }
    setErro(null);
    salvarM.mutate();
  }

  return (
    <div className="space-y-2.5">
      {form.descricao && <p className="text-[11px] text-slate-500 italic">{form.descricao}</p>}
      {form.campos.map((c) => (
        <CampoInput key={c.id} campo={c} valor={valores[c.id || ""]} onChange={(v) => set(c.id || "", v)} />
      ))}
      {erro && <p className="text-[11px] text-red-600">{erro}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button type="button" onClick={submeter} disabled={salvarM.isPending} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50">
          <Check size={13} /> {salvarM.isPending ? "Salvando…" : "Salvar briefing"}
        </button>
        <button type="button" onClick={onCancelar} className="px-3 py-1.5 rounded text-xs bg-slate-100 dark:bg-slate-700 hover:bg-slate-200">Cancelar</button>
      </div>
    </div>
  );
}

/**
 * Só os CAMPOS do formulário, controlados de fora (sem botão de salvar). Usado
 * na CRIAÇÃO da tarefa, onde o briefing é preenchido junto do resto do form e
 * salvo depois que a tarefa ganha id. Ver NovaTarefaModal.
 */
export function CamposBriefingInline({ form, valores, onChange }: {
  form: FormularioBriefing;
  valores: Record<string, any>;
  onChange: (campoId: string, v: any) => void;
}) {
  return (
    <div className="space-y-2.5">
      {form.descricao && <p className="text-[11px] text-slate-500 italic">{form.descricao}</p>}
      {form.campos.map((c) => (
        <CampoInput key={c.id} campo={c} valor={valores[c.id || ""]} onChange={(v) => onChange(c.id || "", v)} />
      ))}
    </div>
  );
}

function CampoInput({ campo, valor, onChange }: { campo: CampoBriefing; valor: any; onChange: (v: any) => void }) {
  const ops = opcoesDoCampo(campo);
  const base = "w-full px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-xs outline-none focus:border-blue-500";
  const rotulo = (
    <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-300 mb-0.5">
      {campo.rotulo} {campo.obrigatorio && <span className="text-red-500">*</span>}
      {campo.ajuda && <span className="block text-[10px] font-normal text-slate-400">{campo.ajuda}</span>}
    </label>
  );

  if (campo.tipo === "paragrafo") {
    return <div>{rotulo}<textarea rows={3} value={valor || ""} onChange={(e) => onChange(e.target.value)} className={base} /></div>;
  }
  if (campo.tipo === "data") {
    return <div>{rotulo}<input type="date" value={valor || ""} onChange={(e) => onChange(e.target.value)} className={base} /></div>;
  }
  if (campo.tipo === "numero") {
    return <div>{rotulo}<input type="number" value={valor || ""} onChange={(e) => onChange(e.target.value)} className={base} /></div>;
  }
  if (campo.tipo === "sim_nao") {
    return (
      <div>{rotulo}
        <div className="flex gap-3">
          {["Sim", "Não"].map((o) => (
            <label key={o} className="flex items-center gap-1 text-xs cursor-pointer">
              <input type="radio" checked={valor === o} onChange={() => onChange(o)} /> {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (campo.tipo === "lista") {
    return (
      <div>{rotulo}
        <select value={valor || ""} onChange={(e) => onChange(e.target.value)} className={base}>
          <option value="">— selecione</option>
          {ops.map((o) => <option key={o} value={o}>{o}</option>)}
          {campo.permiteOutro && <option value={OUTRO}>Outro</option>}
        </select>
        {valor === OUTRO && <input placeholder="Qual?" onChange={(e) => onChange(e.target.value)} className={`${base} mt-1`} />}
      </div>
    );
  }
  if (campo.tipo === "escolha_unica") {
    return (
      <div>{rotulo}
        <div className="space-y-0.5">
          {ops.map((o) => (
            <label key={o} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="radio" checked={valor === o} onChange={() => onChange(o)} /> {o}
            </label>
          ))}
          {campo.permiteOutro && (
            <label className="flex items-center gap-1.5 text-xs">
              <input type="radio" checked={!!valor && !ops.includes(valor)} onChange={() => onChange("")} />
              Outro:
              <input value={ops.includes(valor) ? "" : valor || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-xs outline-none" />
            </label>
          )}
        </div>
      </div>
    );
  }
  if (campo.tipo === "caixas") {
    const arr: string[] = Array.isArray(valor) ? valor : [];
    const toggle = (o: string) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div>{rotulo}
        <div className="space-y-0.5">
          {ops.map((o) => (
            <label key={o} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} /> {o}
            </label>
          ))}
          {campo.permiteOutro && (
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-slate-500">Outro:</span>
              <input
                value={arr.find((x) => !ops.includes(x)) || ""}
                onChange={(e) => {
                  const base2 = arr.filter((x) => ops.includes(x));
                  onChange(e.target.value ? [...base2, e.target.value] : base2);
                }}
                className="flex-1 px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-xs outline-none"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
  // texto (default)
  return <div>{rotulo}<input value={valor || ""} onChange={(e) => onChange(e.target.value)} className={base} /></div>;
}
